import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { JsonEditor } from '../../_shared/components/JsonEditor';
import { consumerGroupFormSchema } from '../schema/consumerGroupSchema';
import { useCreateConsumerGroup, useReplaceConsumerGroup } from '../hooks/useConsumerGroups';
import type { ConsumerGroupFormValues } from '../schema/consumerGroupSchema';
import type { ConsumerGroupRow } from '../types/consumerGroup';

interface ConsumerGroupFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: ConsumerGroupRow | null;
  onClose: () => void;
  onSaved?: () => void;
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: 'var(--spacing-2) var(--spacing-3)',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-brand-primary)' : '2px solid transparent',
        background: 'none',
        color: active ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
        cursor: 'pointer',
        fontSize: 'var(--font-size-sm)',
        fontWeight: active ? 600 : 400,
      }}
    >
      {children}
    </button>
  );
}

const DEFAULT_VALUES: ConsumerGroupFormValues = {
  name: '',
  description: '',
  enabled: true,
};

export function ConsumerGroupFormDialog({ open, mode, initial, onClose, onSaved }: ConsumerGroupFormDialogProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'basic' | 'json'>('basic');
  const [toast, setToast] = useState<string | null>(null);

  const form = useForm<ConsumerGroupFormValues>({
    resolver: zodResolver(consumerGroupFormSchema),
    defaultValues: initial
      ? { name: initial.name, description: initial.description ?? '', enabled: initial.enabled }
      : DEFAULT_VALUES,
  });

  const createGroup = useCreateConsumerGroup((name) => {
    setToast(t('gateway.consumerGroups.toast.created', { name }));
    setTimeout(() => { setToast(null); onSaved?.(); onClose(); }, 1500);
  });
  const replaceGroup = useReplaceConsumerGroup(initial?.id ?? '', () => {
    setToast(t('gateway.consumerGroups.toast.updated'));
    setTimeout(() => { setToast(null); onSaved?.(); onClose(); }, 1500);
  });

  useEffect(() => {
    if (open) {
      setActiveTab('basic');
      form.reset(initial
        ? { name: initial.name, description: initial.description ?? '', enabled: initial.enabled }
        : DEFAULT_VALUES);
    }
  }, [open, initial, form]);

  const watchedValues = form.watch();
  const isPending = createGroup.isPending || replaceGroup.isPending;

  const onSubmit = form.handleSubmit((values) => {
    if (mode === 'create') {
      createGroup.mutate(values);
    } else if (initial) {
      replaceGroup.mutate(values);
    }
  });

  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    width: '100%',
    padding: 'var(--spacing-2) var(--spacing-3)',
    border: `1px solid ${hasError ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    boxSizing: 'border-box',
  });

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    marginBottom: 'var(--spacing-1)',
  };

  const fieldStyle: React.CSSProperties = { marginBottom: 'var(--spacing-3)' };

  return (
    <FormDialog
      open={open}
      title={mode === 'create' ? t('gateway.consumerGroups.form.create') : t('gateway.consumerGroups.form.edit')}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={isPending ? t('gateway.shared.actions.saving') : t('gateway.shared.actions.save')}
      cancelLabel={t('gateway.shared.actions.cancel')}
    >
      {toast && (
        <div role="alert" style={{ marginBottom: 'var(--spacing-3)', padding: 'var(--spacing-2) var(--spacing-3)', background: 'var(--color-success)', color: 'var(--color-text-inverse)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)' }}>
          {toast}
        </div>
      )}

      {/* Tabs */}
      <div role="tablist" style={{ display: 'flex', borderBottom: '1px solid var(--color-surface-border)', marginBottom: 'var(--spacing-4)' }}>
        <TabButton active={activeTab === 'basic'} onClick={() => setActiveTab('basic')}>{t('gateway.consumerGroups.form.tabs.basic')}</TabButton>
        <TabButton active={activeTab === 'json'} onClick={() => setActiveTab('json')}>JSON</TabButton>
      </div>

      {/* Basic Tab */}
      {activeTab === 'basic' && (
        <div role="tabpanel">
          <div style={fieldStyle}>
            <label htmlFor="cg-name" style={labelStyle}>{t('gateway.consumerGroups.form.name.label')}</label>
            <input
              id="cg-name"
              {...form.register('name')}
              placeholder="premium-users"
              aria-invalid={!!form.formState.errors.name}
              aria-describedby={form.formState.errors.name ? 'cg-name-error' : undefined}
              style={inputStyle(!!form.formState.errors.name)}
            />
            {form.formState.errors.name && (
              <span id="cg-name-error" role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>
                {form.formState.errors.name.message}
              </span>
            )}
          </div>

          <div style={fieldStyle}>
            <label htmlFor="cg-description" style={labelStyle}>{t('gateway.consumerGroups.form.description.label')}</label>
            <input
              id="cg-description"
              {...form.register('description')}
              placeholder="그룹 설명 입력"
              style={inputStyle(false)}
            />
          </div>

          <div style={fieldStyle}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)' }}>
              <input type="checkbox" {...form.register('enabled')} />
              {t('gateway.consumerGroups.form.enabled.label')}
            </label>
          </div>
        </div>
      )}

      {/* JSON Tab */}
      {activeTab === 'json' && (
        <div role="tabpanel">
          <JsonEditor value={watchedValues} readOnly height={240} ariaLabel={t('gateway.shared.json.preview')} />
        </div>
      )}
    </FormDialog>
  );
}
